import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useContactInfo, useCompanyName } from '../contexts/SettingsContext';
import { renderCountryFlag, getFlagUrl } from '../utils/flagUtils';
import { apiService } from '../services/api';

interface Office {
  id: number;
  country_id: number;
  country: string;
  country_code: string;
  flag_emoji: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  timezone: string | null;
  display_order: number;
}

const ContactUs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const companyName = useCompanyName();
  const contactInfo = useContactInfo();
  const [offices, setOffices] = useState<Office[]>([]);
  const [isLoadingOffices, setIsLoadingOffices] = useState(true);

  useEffect(() => {
    // Scroll to the message form section when page loads
    const messageSection = document.getElementById('message-form');
    if (messageSection) {
      messageSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Fetch offices from new API endpoint
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        // Normalize language code (en-US -> en, ar-SA -> ar)
        const lang = i18n.language.split('-')[0];

        const response = await fetch(
          `https://patrixglobal.com/backend/api/offices/public.php?lang=${lang}`
        );
        const data = await response.json();

        if (data.success && data.data.offices) {
          setOffices(data.data.offices);
        }
      } catch (error) {      } finally {
        setIsLoadingOffices(false);
      }
    };

    fetchOffices();
  }, [i18n.language]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    message: '',
  });

  const [countries, setCountries] = useState<Array<{
    id: number;
    name: string;
    name_en: string;
    name_ar: string;
    code: string;
    flag_emoji: string;
    active: boolean;
  }>>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch countries from database
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiService.getCountries(i18n.language);
        if (response.success && response.countries) {
          // Filter only active countries
          setCountries(response.countries.filter(c => c.active));
        }
      } catch (error) {      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, [i18n.language]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCountrySelect = (countryCode: string) => {
    setFormData({
      ...formData,
      country: countryCode,
    });
    setIsDropdownOpen(false);
  };

  const getSelectedCountryName = () => {
    if (!formData.country) return t('contactUs.form.selectCountry');
    if (formData.country === 'other') return t('contactUs.form.other');
    const country = countries.find(c => c.code === formData.country);
    return country ? (i18n.language === 'ar' ? country.name_ar : country.name_en) : t('contactUs.form.selectCountry');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Get current language (en or ar)
      const currentLanguage = i18n.language.split('-')[0]; // Convert 'en-US' to 'en', 'ar-SA' to 'ar'

      const response = await fetch('https://patrixglobal.com/backend/api/contact/submit.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: currentLanguage
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          country: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');      }
    } catch (error) {      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: '📧',
      title: t('contactUs.contactMethods.emailUs'),
      description: t('contactUs.contactMethods.emailDesc'),
      contact: contactInfo.main_email || 'info@patrixglobal.com',
      action: `mailto:${contactInfo.main_email || 'info@patrixglobal.com'}`,
    },
    {
      icon: '📞',
      title: t('contactUs.contactMethods.callUs'),
      description: t('contactUs.contactMethods.callDesc'),
      contact: contactInfo.main_phone || '+1 (800) 123-4567',
      action: `tel:${(contactInfo.main_phone || '+1 (800) 123-4567').replace(/\s+/g, '')}`,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('contactUs.title')}
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto">
              {t('contactUs.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('contactUs.getInTouch')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('contactUs.chooseWay')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {contactMethods.map((method, index) => (
              <motion.a
                key={index}
                href={method.action}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300 block"
              >
                <div className="text-5xl mb-6">{method.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {method.title}
                </h3>
                <p className="text-gray-600 mb-6 text-lg">{method.description}</p>
                <p className="text-primary-600 font-semibold text-lg">{method.contact}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Office Info */}
      <section id="message-form" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {t('contactUs.sendMessage')}
              </h2>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
                >
                  <div className="flex items-center">
                    <div className="text-green-600 mr-3">✓</div>
                    <p className="text-green-800">
                      {t('contactUs.messageSent')}
                    </p>
                  </div>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
                >
                  <div className="flex items-center">
                    <div className="text-red-600 mr-3">✕</div>
                    <p className="text-red-800">
                      {t('contactUs.messageError') || 'Failed to send message. Please try again later.'}
                    </p>
                  </div>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contactUs.labels.fullName')} {t('contactUs.form.required')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder={t('contactUs.placeholders.fullName')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contactUs.labels.email')} {t('contactUs.form.required')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder={t('contactUs.placeholders.email')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contactUs.form.phoneNumber')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder={t('contactUs.placeholders.phone')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contactUs.form.companyName')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder={t('contactUs.placeholders.company')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contactUs.form.countryOfInterest')}
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => !isLoadingCountries && setIsDropdownOpen(!isDropdownOpen)}
                      disabled={isLoadingCountries}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-left flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        {formData.country && formData.country !== 'other' && (
                          <img
                            src={getFlagUrl(formData.country)}
                            alt=""
                            className="w-6 h-4 object-cover rounded-sm"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <span className={formData.country ? 'text-gray-900' : 'text-gray-500'}>
                          {isLoadingCountries ? t('contactUs.form.loadingCountries') || 'Loading countries...' : getSelectedCountryName()}
                        </span>
                      </div>
                      <svg className="fill-current h-4 w-4 text-gray-700 transition-transform" style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                      </svg>
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div
                          onClick={() => handleCountrySelect('')}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors text-gray-500"
                        >
                          {t('contactUs.form.selectCountry')}
                        </div>
                        {countries.map((country) => (
                          <div
                            key={country.id}
                            onClick={() => handleCountrySelect(country.code)}
                            className={`px-4 py-3 hover:bg-primary-50 cursor-pointer transition-colors flex items-center space-x-3 rtl:space-x-reverse ${
                              formData.country === country.code ? 'bg-primary-100' : ''
                            }`}
                          >
                            <img
                              src={getFlagUrl(country.code)}
                              alt={`${country.name_en} flag`}
                              className="w-6 h-4 object-cover rounded-sm flex-shrink-0"
                              onError={(e) => {
                                const target = e.currentTarget;
                                target.style.display = 'none';
                                const fallback = document.createElement('span');
                                fallback.textContent = country.flag_emoji;
                                fallback.className = 'text-lg';
                                target.parentNode?.appendChild(fallback);
                              }}
                            />
                            <span className="text-gray-900">
                              {i18n.language === 'ar' ? country.name_ar : country.name_en}
                            </span>
                          </div>
                        ))}
                        <div
                          onClick={() => handleCountrySelect('other')}
                          className={`px-4 py-3 hover:bg-primary-50 cursor-pointer transition-colors ${
                            formData.country === 'other' ? 'bg-primary-100' : ''
                          }`}
                        >
                          {t('contactUs.form.other')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contactUs.labels.message')} {t('contactUs.form.required')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 resize-none"
                    placeholder={t('contactUs.placeholders.message')}
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('contactUs.form.sending')}</span>
                    </div>
                  ) : (
                    t('contactUs.form.sendMessage')
                  )}
                </motion.button>
              </form>
            </motion.div>

            {/* Office Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
              <h2 className={`text-3xl font-bold text-gray-900 mb-8 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                {t('contactUs.globalOffices')}
              </h2>

              {isLoadingOffices ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : offices.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {i18n.language === 'ar' ? 'لا توجد مكاتب متاحة حالياً' : 'No offices available at the moment'}
                </p>
              ) : (
                <div className="space-y-6">
                  {offices.map((office, index) => (
                    <motion.div
                      key={office.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                    >
                      <div className={`flex items-start ${i18n.language === 'ar' ? 'space-x-reverse' : ''} space-x-6`}>
                        <div className="w-12 h-8 flex items-center justify-center flex-shrink-0">
                          {renderCountryFlag(
                            office.country_code,
                            office.flag_emoji,
                            office.country,
                            'w-12 h-8 object-cover rounded'
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-xl font-bold text-gray-900 mb-1 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {office.city}, {office.country}
                          </h3>
                          <p className={`text-gray-600 mb-3 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                            {office.address}
                          </p>
                          <div className={`space-y-2 ${i18n.language === 'ar' ? 'w-full' : ''}`}>
                            <div className={`flex items-center gap-2`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                              <span className="text-primary-600">📞</span>
                              <a
                                href={`tel:${office.phone.replace(/\s+/g, '')}`}
                                className="text-gray-700 hover:text-primary-600 transition-colors"
                                dir="ltr"
                              >
                                {office.phone}
                              </a>
                            </div>
                            <div className={`flex items-center gap-2`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                              <span className="text-primary-600">📧</span>
                              <a
                                href={`mailto:${office.email}`}
                                className="text-gray-700 hover:text-primary-600 transition-colors"
                                dir="ltr"
                              >
                                {office.email}
                              </a>
                            </div>
                            {office.timezone && (
                              <div className={`flex items-center gap-2`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                                <span className="text-primary-600">🕒</span>
                                <span className="text-gray-700" dir="ltr">{office.timezone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('contactUs.faq.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('contactUs.faq.subtitle')}
            </p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: t('contactUs.faq.questions.formationTime'),
                answer: t('contactUs.faq.questions.formationTimeAnswer')
              },
              {
                question: t('contactUs.faq.questions.documents'),
                answer: t('contactUs.faq.questions.documentsAnswer')
              },
              {
                question: t('contactUs.faq.questions.ongoingSupport'),
                answer: t('contactUs.faq.questions.ongoingSupportAnswer')
              },
              {
                question: t('contactUs.faq.questions.nonResident'),
                answer: t('contactUs.faq.questions.nonResidentAnswer')
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;